import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, type RfiRow, type CoRow, type EstimateRow, type QcReview } from '../api/client';
import { exportInternalBom, exportRfiLog, exportCoLog, downloadBlob } from '../lib/exports';

const money = (n: number | null | undefined) => n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

// Phase 7 — outputs. Supplier Pricing PDF (external-safe), Full Internal BOM,
// RFIs, potential change orders, and the Estimate Summary.
export function Outputs() {
  const { id = '' } = useParams();
  const [rfis, setRfis] = useState<RfiRow[]>([]);
  const [cos, setCos] = useState<CoRow[]>([]);
  const [estimate, setEstimate] = useState<EstimateRow | null>(null);
  const [qc, setQc] = useState<QcReview | null>(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try {
      const [r, c, e, q] = await Promise.all([api.getRfis(id), api.getChangeOrders(id), api.getEstimate(id), api.getQcReview(id)]);
      setRfis(r.rfis); setCos(c.change_orders); setEstimate(e.estimate); setQc(q);
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load'); }
  }
  useEffect(() => { void load(); }, [id]);

  async function run(label: string, fn: () => Promise<unknown>) {
    setBusy(label); setError('');
    try { await fn(); await load(); } catch (e) { setError(e instanceof Error ? e.message : `${label} failed`); }
    finally { setBusy(''); }
  }

  async function exportBom() {
    setBusy('Building internal BOM…'); setError('');
    try {
      const b = await api.getBom(id);
      if (!b.lines.length) { setError('No BOM yet — generate it first.'); return; }
      downloadBlob(await exportInternalBom(estimate?.costing ? 'Project' : 'Project', b.lines, today()), 'Internal_BOM.xlsx');
    } catch (e) { setError(e instanceof Error ? e.message : 'Export failed'); }
    finally { setBusy(''); }
  }

  return (
    <div>
      <Link to={`/projects/${id}/costing`} className="muted" style={{ fontSize: 13 }}>← Pricing & labor</Link>
      <h1 style={{ margin: '4px 0 0' }}>Outputs</h1>
      <p className="muted" style={{ marginTop: 4 }}>Supplier RFQ, internal BOM, RFIs, change-order risks and the estimate. Internal documents are never sent to customers.</p>
      {error && <div className="error">{error}</div>}

      {/* Estimate + documents */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Estimate &amp; documents</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button className="btn btn-primary" disabled={!!busy} onClick={() => run('Saving estimate…', () => api.saveEstimate(id))}>
            {busy === 'Saving estimate…' ? busy : 'Save Estimate'}
          </button>
          <a className="btn btn-ghost" href={api.pdfUrl(id, 'supplier-pdf')}>Supplier Pricing PDF (Button 1)</a>
          <button className="btn btn-ghost" disabled={!!busy} onClick={exportBom}>Full Internal BOM (Button 2)</button>
          <a className="btn btn-ghost" href={api.pdfUrl(id, 'estimate-pdf')}>Internal Estimate PDF</a>
        </div>
        {estimate && (
          <div className="banner" style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Estimate v{estimate.version} saved {estimate.created_at}.</span>
            <strong style={{ fontSize: 18, color: 'var(--teal-dark)' }}>{money(estimate.final_sell_price)}</strong>
          </div>
        )}
        <p className="muted" style={{ marginBottom: 0, fontSize: 12 }}>
          The Supplier PDF lists material quantities only — no labor, pricing, markup or bid total. The internal documents are confidential.
        </p>
      </div>

      {/* Proposals — final number only */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h2>Proposals <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>— final number only; no internal breakdown</span></h2>
        {!estimate && <p className="error">Save an estimate first — proposals show the final number, which comes from the estimate.</p>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <a className="btn btn-gold" href={api.pdfUrl(id, 'proposal-short-pdf')}>Short Proposal (3–4 pp)</a>
          <a className="btn btn-gold" href={api.pdfUrl(id, 'proposal-full-pdf')}>Full Proposal (20+ pp)</a>
        </div>
        <p className="muted" style={{ marginBottom: 0, fontSize: 12 }}>
          Company content comes from the editable Company Profile (Admin) — the software invents no company facts.
        </p>
      </div>

      {/* QC bid review */}
      {qc && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0 }}>QC bid review</h2>
            <div className="muted">{qc.summary.pass} pass · <span style={{ color: 'var(--warning)' }}>{qc.summary.warn} warn</span> · <span style={{ color: 'var(--danger)' }}>{qc.summary.fail} fail</span></div>
          </div>
          <table style={{ marginTop: 8 }}>
            <tbody>
              {qc.checks.map((ch) => (
                <tr key={ch.key}>
                  <td style={{ width: 24 }}>{ch.status === 'pass' ? '✅' : ch.status === 'warn' ? '⚠️' : '❌'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}><strong>{ch.label}</strong></td>
                  <td className="muted">{ch.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* RFIs */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>RFIs <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({rfis.length})</span></h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => run('Generating RFIs…', () => api.generateRfis(id))}>Generate RFIs</button>
            <button className="btn btn-ghost btn-sm" disabled={!rfis.length} onClick={async () => downloadBlob(await exportRfiLog('Project', rfis, today()), 'RFI_Log.xlsx')}>Export</button>
          </div>
        </div>
        {!rfis.length && <p className="muted">No RFIs yet. Generate from the analysis flags.</p>}
        {rfis.map((r) => (
          <div key={r.id} style={{ borderLeft: '3px solid var(--gold)', padding: '6px 10px', marginTop: 8 }}>
            <div><strong>{r.title}</strong></div>
            <div>{r.question}</div>
            <div className="muted" style={{ fontSize: 12 }}>{r.reason} · {r.source_ref} · {r.cost_schedule_impact}</div>
          </div>
        ))}
      </div>

      {/* Change orders */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>Potential change orders <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>({cos.length})</span></h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => run('Generating change orders…', () => api.generateChangeOrders(id))}>Generate</button>
            <button className="btn btn-ghost btn-sm" disabled={!cos.length} onClick={async () => downloadBlob(await exportCoLog('Project', cos, today()), 'Change_Order_Log.xlsx')}>Export</button>
          </div>
        </div>
        {!cos.length && <p className="muted">No change-order risks yet. Generate from alternates, allowances and scope flags.</p>}
        {cos.map((c) => (
          <div key={c.id} style={{ borderLeft: `3px solid ${c.risk_level === 'high' ? 'var(--danger)' : 'var(--gold)'}`, padding: '6px 10px', marginTop: 8 }}>
            <div><strong>{c.title}</strong> <span className={`pill pill-${c.risk_level === 'high' ? 'needs_review' : 'proposed'}`}>{c.risk_level}</span></div>
            <div>{c.description}</div>
            <div className="muted" style={{ fontSize: 12 }}>{c.why} · {c.source_ref} · {c.recommendation}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
